import { Link, NavLink } from "react-router-dom";
import { Film, UserRound, CircleHelp, Shield, Plus, Users } from "lucide-react";
import Navbar, { Logo } from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";

const L = {
  fr: { newProject: "Nouveau projet", projects: "Mes projets", shared: "Partagés", account: "Mon compte", help: "Aide", admin: "Admin", newShort: "Nouveau" },
  en: { newProject: "New project", projects: "My projects", shared: "Shared", account: "My account", help: "Help", admin: "Admin", newShort: "New" },
};

function navItems(user, l) {
  return [
    { to: "/projects", icon: Film, label: l.projects, id: "projects" },
    ...(user.has_shared ? [{ to: "/shared", icon: Users, label: l.shared, id: "shared" }] : []),
    { to: "/dashboard", icon: UserRound, label: l.account, id: "account" },
    { to: "/aide", icon: CircleHelp, label: l.help, id: "help" },
    ...(user.role === "admin" ? [{ to: "/admin", icon: Shield, label: l.admin, id: "admin" }] : []),
  ];
}

const sideCls = ({ isActive }) =>
  `group flex items-center gap-3 h-11 px-3 font-osd text-[11px] tracking-[0.14em] uppercase transition-colors ${
    isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"
  }`;

export default function AppShell({ children }) {
  const { user } = useAuth();
  const { lang } = useI18n();
  const l = L[lang] || L.fr;
  if (!user) {
    return (
      <div className="min-h-screen bg-background text-foreground flex flex-col">
        <Navbar />
        <main className="flex-1">{children}</main>
        <div className="hidden md:block"><Footer /></div>
      </div>
    );
  }
  const items = navItems(user, l);
  return (
    <div className="min-h-screen bg-background text-foreground md:flex">
      <aside className="hidden md:flex flex-col w-60 shrink-0 border-r border-border sticky top-0 h-screen px-5 py-6" data-testid="app-sidebar">
        <Logo />
        <Link
          to="/start"
          data-testid="sidebar-new-project"
          className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-primary text-white font-osd text-[11px] tracking-[0.08em] uppercase h-11 px-5 hover:opacity-90 transition-opacity"
        >
          <Plus size={14} /> {l.newProject}
        </Link>
        <nav className="mt-8 flex flex-col gap-1">
          {items.map(({ to, icon: Icon, label, id }) => (
            <NavLink key={to} to={to} className={sideCls} data-testid={`sidebar-${id}`}>
              {({ isActive }) => (
                <>
                  <span className={`h-1.5 w-1.5 shrink-0 ${isActive ? "bg-primary" : "bg-transparent"}`} aria-hidden="true" />
                  <Icon size={15} /> {label}
                </>
              )}
            </NavLink>
          ))}
        </nav>
      </aside>
      <div className="flex-1 min-w-0 flex flex-col">
        <main className="flex-1 pb-28 md:pb-0">{children}</main>
        <div className="hidden md:block"><Footer /></div>
      </div>
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-border bg-background/95 backdrop-blur-xl grid"
        style={{ gridTemplateColumns: `repeat(${items.length + 1}, minmax(0, 1fr))`, paddingTop: "6px", paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 18px)" }}
        data-testid="app-tabbar"
      >
        <Link to="/start" data-testid="tabbar-new-project" className="flex flex-col items-center justify-center gap-1 h-14 text-primary">
          <span className="grid place-items-center h-7 w-7 rounded-full bg-primary text-white"><Plus size={15} /></span>
          <span className="font-osd text-[10px] tracking-wider uppercase">{l.newShort}</span>
        </Link>
        {items.map(({ to, icon: Icon, label, id }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={`tabbar-${id}`}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-1 h-14 ${isActive ? "text-foreground" : "text-muted-foreground"}`
            }
          >
            <Icon size={18} />
            <span className="font-osd text-[10px] tracking-wider uppercase truncate max-w-full px-1">{label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
